if (!customElements.get('vehicle-buying-panel')) {
  class VehicleBuyingPanel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.vehicleInitialized === 'true') return;
      this.dataset.vehicleInitialized = 'true';

      if (!window.__ignitionFinanceOpenBound) {
        window.__ignitionFinanceOpenBound = true;
        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-finance-open]');
          if (!trigger) return;
          const calculator = document.getElementById('vehicle-finance-calculator');
          if (!calculator) return;
          event.preventDefault();
          calculator.open = true;
          const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          calculator.scrollIntoView({ behavior: motion, block: 'center' });
        });
      }

      this.priceCents = Number(this.dataset.priceCents || 0);
      this.freightCents = Number(this.dataset.freightCents || 0);
      this.pdiCents = Number(this.dataset.pdiCents || 0);
      this.documentationCents = Number(this.dataset.documentationCents || 0);
      this.vsaCents = Number(this.dataset.vsaCents || 0);
      this.tireEcoFeeCents = Number(this.dataset.tireEcoFeeCents || 0);
      this.taxRatePercent = Number(this.dataset.taxRatePercent || 12);
      this.rebateCents = Number(this.dataset.rebateCents || 0);
      this.apr = Number(this.dataset.apr || 0);
      this.termMonths = Number(this.dataset.termMonths || 0);

      this.aprInput = this.querySelector('[data-finance-apr]');
      this.termInput = this.querySelector('[data-finance-term]');
      this.downInput = this.querySelector('[data-finance-down]');
      this.tradeInput = this.querySelector('[data-finance-trade]');
      this.frequencyInput = this.querySelector('[data-finance-frequency]');

      [this.aprInput, this.termInput, this.downInput, this.tradeInput, this.frequencyInput].forEach((el) => {
        if (el) el.addEventListener('input', () => this.updatePayment());
        if (el) el.addEventListener('change', () => this.updatePayment());
      });

      this.reserveTriggers = [...this.querySelectorAll('[data-vehicle-reserve]')];
      this.reserveTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.openReservationReview(trigger));
      });

      const reserveConfirm = this.querySelector('[data-reservation-confirm]');
      if (reserveConfirm) reserveConfirm.addEventListener('click', () => this.reserveVehicle(reserveConfirm));

      const reserveCancel = this.querySelector('[data-reservation-cancel]');
      if (reserveCancel) reserveCancel.addEventListener('click', () => this.closeReservationReview());

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.querySelector('[data-reservation-review]')?.hidden) {
          this.closeReservationReview();
        }
      });

      this.updatePayment();
    }

    openReservationReview(trigger) {
      const review = this.querySelector('[data-reservation-review]');
      if (!review) return;
      this.activeReserveTrigger = trigger || this.reserveTriggers?.[0];
      review.hidden = false;
      this.reserveTriggers?.forEach((item) => item.setAttribute('aria-expanded', item === this.activeReserveTrigger ? 'true' : 'false'));
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      review.scrollIntoView({ behavior: motion, block: 'center' });
      review.focus({ preventScroll: true });
    }

    closeReservationReview() {
      const review = this.querySelector('[data-reservation-review]');
      if (review) review.hidden = true;
      this.reserveTriggers?.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      const trigger = this.activeReserveTrigger || this.reserveTriggers?.[0];
      if (trigger) trigger.focus({ preventScroll: true });
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
          style: 'currency',
          currency: 'CAD',
          maximumFractionDigits: 2
        }).format(amount);
      } catch (e) {
        return `$${amount.toFixed(2)}`;
      }
    }

    financePrincipalCents() {
      const downCents = Math.max(0, Number(this.downInput?.value || 0) * 100);
      const tradeCents = Math.max(0, Number(this.tradeInput?.value || 0) * 100);
      const grossBeforeTax = this.priceCents + this.freightCents + this.pdiCents + this.documentationCents + this.vsaCents + this.tireEcoFeeCents - this.rebateCents;
      const taxMultiplier = 1 + (Math.max(0, this.taxRatePercent) / 100);
      const grossWithTax = Math.round(grossBeforeTax * taxMultiplier);
      return Math.max(0, grossWithTax - downCents - tradeCents);
    }

    paymentFor(principalCents, annualRate, months, paymentsPerYear) {
      const principal = principalCents / 100;
      if (!principal || !months || !paymentsPerYear) return 0;
      const totalPayments = (months / 12) * paymentsPerYear;
      const periodicRate = annualRate > 0 ? annualRate / 100 / paymentsPerYear : 0;
      if (periodicRate === 0) return principal / totalPayments;
      return principal * periodicRate / (1 - Math.pow(1 + periodicRate, -totalPayments));
    }

    updatePayment() {
      const aprValue = this.aprInput ? this.aprInput.value : this.dataset.apr;
      const termValue = this.termInput ? this.termInput.value : this.dataset.termMonths;
      this.apr = Number(aprValue);
      this.termMonths = Number(termValue);

      if (aprValue === '' || termValue === '' || !Number.isFinite(this.apr) || !Number.isFinite(this.termMonths) || this.apr < 0 || this.termMonths <= 0) {
        this.querySelectorAll('[data-payment-summary]').forEach((el) => { el.textContent = 'Enter rate'; });
        const result = this.querySelector('[data-finance-payment]');
        const assumptions = this.querySelector('[data-finance-assumptions]');
        if (result) result.textContent = '—';
        if (assumptions) assumptions.textContent = 'Enter an APR and term to calculate a payment';
        return;
      }
      const frequency = Number(this.frequencyInput?.value || 26);
      const principalCents = this.financePrincipalCents();
      const payment = this.paymentFor(principalCents, this.apr, this.termMonths, frequency);
      const frequencyText = frequency === 52 ? 'weekly' : frequency === 12 ? 'monthly' : 'biweekly';

      this.querySelectorAll('[data-payment-summary]').forEach((el) => { el.textContent = this.money(payment * 100); });
      const result = this.querySelector('[data-finance-payment]');
      const amount = this.querySelector('[data-finance-amount]');
      const frequencyLabel = this.querySelector('[data-finance-frequency-label]');
      if (result) result.textContent = this.money(payment * 100);
      if (amount) amount.textContent = this.money(principalCents);
      if (frequencyLabel) frequencyLabel.textContent = frequencyText;
      const assumptions = this.querySelector('[data-finance-assumptions]');
      if (assumptions) assumptions.textContent = `${this.apr}% APR for ${this.termMonths} months`;

      const lead = this.querySelector('[data-finance-lead]');
      if (lead) {
        const url = new URL(this.dataset.financeBaseUrl, window.location.origin);
        url.searchParams.set('source', 'get_this_payment');
        if (this.dataset.productTitle) url.searchParams.set('vehicle', this.dataset.productTitle);
        if (this.dataset.variantTitle) url.searchParams.set('variant', this.dataset.variantTitle);
        if (this.dataset.stockNumber) url.searchParams.set('stock', this.dataset.stockNumber);
        if (this.dataset.productId) url.searchParams.set('product_id', this.dataset.productId);
        const financeVariantId = this.dataset.vehicleVariantId || this.dataset.variantId;
        if (financeVariantId) url.searchParams.set('variant_id', financeVariantId);
        if (this.dataset.productUrl) url.searchParams.set('product_url', this.dataset.productUrl);
        if (this.dataset.vendor) url.searchParams.set('vendor', this.dataset.vendor);
        if (this.dataset.productType) url.searchParams.set('product_type', this.dataset.productType);
        if (this.dataset.pageType) url.searchParams.set('page_type', this.dataset.pageType);
        if (this.dataset.crmKey) url.searchParams.set('crm_key', this.dataset.crmKey);
        if (this.dataset.financeProgram) url.searchParams.set('finance_program', this.dataset.financeProgram);
        url.searchParams.set('apr', this.apr.toString());
        url.searchParams.set('term', this.termMonths.toString());
        url.searchParams.set('frequency', frequencyText);
        url.searchParams.set('payment', payment.toFixed(2));
        url.searchParams.set('down_payment', Number(this.downInput?.value || 0).toFixed(2));
        url.searchParams.set('trade_equity', Number(this.tradeInput?.value || 0).toFixed(2));
        url.searchParams.set('amount_financed', (principalCents / 100).toFixed(2));
        url.searchParams.set('tax_rate', this.taxRatePercent.toFixed(2));
        url.searchParams.set('tax_included', 'true');
        lead.href = url.toString();
      }
    }

    async reserveVehicle(button) {
      const depositVariantId = Number(this.dataset.depositVariantId || 0);
      if (!depositVariantId) return;
      const tireEcoRequired = this.dataset.tireEcoRequired === 'true';
      const tireEcoVariantId = Number(this.dataset.tireEcoVariantId || 0);
      const tireCount = Number(this.dataset.tireCount || 0);
      if (tireEcoRequired && (!tireEcoVariantId || ![2, 4].includes(tireCount))) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Checking cart…';

      try {
        const cartResponse = await fetch(`${theme.routes.cart}.js`, { headers: { 'Accept': 'application/json' } });
        if (!cartResponse.ok) throw new Error('Unable to verify cart');
        const cart = await cartResponse.json();
        if (Number(cart.item_count || 0) > 0) {
          button.disabled = false;
          button.textContent = originalText;
          alert('Vehicle reservation checkout must contain only the $500 deposit and the required BC tire eco fee. Your existing cart has been left unchanged. Please review or complete that cart, then return to reserve this unit.');
          window.location.assign(theme.routes.cart);
          return;
        }
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.textContent = originalText;
        alert('We could not verify that the reservation checkout is separate from regular merchandise. Please try again or contact Sales.');
        return;
      }

      button.textContent = 'Adding reservation…';

      const vehicleTitle = this.dataset.productTitle || '';
      const variantTitle = this.dataset.variantTitle || '';
      const reservedUnit = variantTitle ? `${vehicleTitle} — ${variantTitle}` : vehicleTitle;
      const taxRate = Number(this.dataset.taxRatePercent || 12);
      const taxRateLabel = Number.isFinite(taxRate) ? `${taxRate}%` : 'estimated';
      const properties = { 'Reserved unit': reservedUnit };
      if (this.dataset.stockNumber) properties['Stock / SKU'] = this.dataset.stockNumber;
      properties['Vehicle status'] = this.dataset.status || '';
      const msrpCents = Number(this.dataset.msrpCents || 0);
      const salePriceCents = Number(this.dataset.salePriceCents || 0);
      if (salePriceCents > 0 && msrpCents > salePriceCents) {
        properties[this.dataset.compareAtLabel || 'MSRP'] = this.money(msrpCents);
        properties['Sale price'] = this.money(salePriceCents);
        properties['Savings'] = this.money(msrpCents - salePriceCents);
      } else {
        properties[this.dataset.vehiclePriceLabel || 'Vehicle price'] = this.money(this.dataset.priceCents);
      }
      if (Number(this.dataset.freightCents || 0) > 0) properties['Freight'] = this.money(this.dataset.freightCents);
      if (Number(this.dataset.pdiCents || 0) > 0) properties['PDI / preparation'] = this.money(this.dataset.pdiCents);
      if (Number(this.dataset.documentationCents || 0) > 0) properties['Documentation fee'] = this.money(this.dataset.documentationCents);
      if (Number(this.dataset.vsaCents || 0) > 0) properties['VSA Levy Recovery'] = this.money(this.dataset.vsaCents);
      if (Number(this.dataset.tireEcoFeeCents || 0) > 0) {
        properties[`BC tire eco fee (${tireCount} × ${this.money(this.dataset.tireEcoFeePerTireCents)})`] = this.money(this.dataset.tireEcoFeeCents);
      }
      properties[`Estimated vehicle tax (${taxRateLabel})`] = this.money(this.dataset.estimatedTaxCents);
      properties['Estimated complete vehicle purchase'] = this.money(this.dataset.estimatedPurchaseTotalCents);
      properties['Reservation deposit paid today — no tax'] = this.money(this.dataset.depositCents || 50000);
      if (Number(this.dataset.tireEcoFeeCents || 0) > 0) {
        properties['BC tire eco fee paid today'] = this.money(this.dataset.tireEcoFeeCents);
        properties['Estimated tax on tire eco fee paid today'] = this.money(this.dataset.tireEcoTaxCents);
      }
      properties['Estimated checkout total today'] = this.money(this.dataset.checkoutDueTodayCents);
      properties['Estimated balance after today’s checkout'] = this.money(this.dataset.estimatedRemainingCents);
      if (this.dataset.crmKey) properties['_CRM Match Key'] = this.dataset.crmKey;
      if (this.dataset.productId) properties['_Vehicle Product ID'] = this.dataset.productId;
      if (this.dataset.vehicleVariantId) properties['_Vehicle Variant ID'] = this.dataset.vehicleVariantId;
      if (this.dataset.productUrl) properties['_Vehicle URL'] = this.dataset.productUrl;
      properties['_Deposit Variant SKU'] = this.dataset.depositVariantSku || '';
      properties['_Vehicle Price Cents'] = this.dataset.priceCents || '0';
      properties['_Freight Cents'] = this.dataset.freightCents || '0';
      properties['_PDI Cents'] = this.dataset.pdiCents || '0';
      properties['_Documentation Cents'] = this.dataset.documentationCents || '0';
      properties['_VSA Cents'] = this.dataset.vsaCents || '0';
      properties['_Tire Count'] = this.dataset.tireCount || '0';
      properties['_Vehicle Page Type'] = this.dataset.pageType || '';
      properties['_Tire Eco Fee Cents'] = this.dataset.tireEcoFeeCents || '0';
      properties['_Tire Eco Tax Cents'] = this.dataset.tireEcoTaxCents || '0';
      properties['_Estimated Tax Cents'] = this.dataset.estimatedTaxCents || '0';

      const items = [{ id: depositVariantId, quantity: 1, properties }];
      if (tireEcoRequired) {
        items.push({
          id: tireEcoVariantId,
          quantity: tireCount,
          properties: {
            'Applied to vehicle': reservedUnit,
            'Fee': `${this.money(this.dataset.tireEcoFeePerTireCents)} per tire`,
            'Tire count': String(tireCount),
            '_CRM Match Key': this.dataset.crmKey || '',
            '_Vehicle Product ID': this.dataset.productId || '',
            '_Vehicle Variant ID': this.dataset.vehicleVariantId || '',
            '_Vehicle Page Type': this.dataset.pageType || '',
            '_Tire Eco Variant SKU': this.dataset.tireEcoVariantSku || ''
          }
        });
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items
          })
        });

        if (!response.ok) throw new Error('Unable to add deposit');
        button.textContent = 'Opening reservation cart…';
        window.location.assign(theme.routes.cart);
      } catch (error) {
        console.error(error);
        button.disabled = false;
        button.textContent = originalText;
        alert('We could not create the reservation cart. Please text or call Ignition Motorsports and we will help you reserve this vehicle.');
      }
    }
  }

  customElements.define('vehicle-buying-panel', VehicleBuyingPanel);
}
